import {computed, Ref, ref, watch} from '@vue/composition-api';
import {Point} from '@/common/types/Point';
import {maxBy, minBy, sortBy} from 'lodash';
import {calculateAxisMinMaxValue} from '@/common/utils/graphs';
import {clone} from '@/common/utils/clone';
import {DataTableHeader} from 'vuetify';

export interface beerPlotWavelength<
  Labels extends Record<Exclude<string, 'slope' | 'intercept'> | number | symbol, string>,
  X extends keyof Labels,
  Y extends keyof Labels,
> {
  labels: Labels;
  x: X;
  y: Y;
  rows: Ref<Row<keyof Labels>[]>;
  isMarking: boolean;
  minY: number | null;
  maxY: number | null;
  minX: number | null;
  maxX: number | null;
}

export type Row<Keys extends string | number | symbol> = {
  [key in Keys]: null | number;
};

export function useBeerPlotWavelength<
  Labels extends Record<Exclude<string, 'slope' | 'intercept'> | number | symbol, string>,
  X extends keyof Labels,
  Y extends keyof Labels,
>({labels, x, y, rows, isMarking, minY, maxY, minX, maxX}: beerPlotWavelength<Labels, X, Y>) {
  const convertToFloat = (value: string | number | null) => {
    if (value === null || typeof value === 'number') {
      return value;
    }
    if (value === '') {
      return null;
    }
    return parseFloat(value);
  };

  const numericRows = computed(() => {
    return rows.value.map((row) => {
      return {
        ...row,
        lambda: convertToFloat(row['lambda']),
        abs: convertToFloat(row['abs']),
      } as Row<keyof Labels>;
    });
  });

  const newRow = {} as Row<keyof Labels>;
  Object.keys(labels).forEach((label) => {
    newRow[label as keyof Row<keyof Labels>] = null;
  });

  watch(
    () => numericRows.value,
    () => {
      const dataPoints = numericRows.value;
      if (!dataPoints || dataPoints.length === 0) {
        rows.value = [clone(newRow)] as Row<keyof Labels>[];
      }
    },
    {
      immediate: true,
    }
  );

  const colDefs: Ref<DataTableHeader[]> = ref([
    {
      text: 'Run',
      value: 'index',
      sortable: false,
    },
  ]);
  Object.entries(labels).forEach(([value, text]) =>
    colDefs.value.push({text, value, sortable: false})
  );

  if (!isMarking) {
    colDefs.value.push({
      text: '',
      value: 'id',
      sortable: false,
    });
  }

  // Extract and sort data points
  const plotPoints: Ref<Point[]> = computed(() => {
    const points = numericRows.value
      .filter((point) => point[x] !== null && point[y] !== null)
      .map((run: any) => {
        return {
          x: run[x],
          y: run[y],
        };
      });
    return sortBy(points, 'x');
  });

  const minYAxis = computed(
    () => minY ?? calculateAxisMinMaxValue(minBy(plotPoints.value, 'y')?.y ?? -10, false)
  );
  const maxYAxis = computed(
    () => maxY ?? calculateAxisMinMaxValue(maxBy(plotPoints.value, 'y')?.y ?? 5)
  );
  const minXAxis = computed(() => minX ?? 0);
  const maxXAxis = computed(
    () => maxX ?? calculateAxisMinMaxValue(maxBy(plotPoints.value, 'x')?.x ?? 100)
  );

  // Create a single line series with the data points
  const apexSeries = computed(() => {
    return [
      {
        name: labels[y],
        type: 'line',
        data: plotPoints.value,
      },
    ];
  });

  const apexOptions = computed(() => {
    return {
      chart: {
        fontFamily: 'serif',
        fontsize: '16px',
        height: 350,
      },
      colors: ['#8B0000'],
      markers: {
        size: 4,
      },
      legend: {
        show: false,
      },
      yaxis: {
        title: {
          text: labels[y],
          style: {
            fontSize: '18px',
            fontWeight: '600',
          },
        },
        type: 'numeric',
        min: minYAxis.value,
        max: maxYAxis.value,
        tickAmount: 10,
        decimalsInFloat: 3,
      },
      xaxis: {
        title: {
          style: {
            fontSize: '14px',
          },
          text: labels[x],
        },
        type: 'numeric',
        min: minXAxis.value,
        max: maxXAxis.value,
        tickAmount: 8,
        decimalsInFloat: 0,
      },
    };
  });

  return {
    colDefs,
    plotPoints,
    apexSeries,
    apexOptions,
  };
}
